import React, { useContext, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import countryList from "react-select-country-list";
import CountrySelectContext from "../utils/CountrySelectContext";
import { createLead } from "../redux/slices/leads";
import NotyfContext from "../contexts/NotyfContext";

const LeadSignUp = ({ show, onHide }) => {
  const countryOptions = useMemo(() => {
    const options = countryList().getData();
    return [{ label: "Select a country", value: "" }, ...options];
  }, []);
  const initialValues = {
    fullName: "",
    telephoneNumber: "",
    email: "",
    country: "",
    username: "",
    consentMarketing: false,
  };

  const notyf = useContext(NotyfContext);

  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      .min(2, "Too short")
      .required("Full name is required"),
    telephoneNumber: Yup.string()
      .matches(/^\+?[0-9\s\-()]+$/, "Invalid phone number")
      .required("Telephone number is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    username: Yup.string().required("Username is required"),
    country: Yup.string().required("Country is required"),
    consentMarketing: Yup.boolean().oneOf(
      [true],
      "You must agree to receive marketing communications"
    ),
  });

  const handleSubmit = async (values, actions) => {
    await createLead({ data: values })
      .then(() => {
        notyf.open({
          type: "success",
          message:
            "Your request has been received. Please check your email to complete your registration.",
          duration: 5000,
          ripple: true,
          dismissible: false,
          position: {
            x: "center",
            y: "top",
          },
        });
      })
      .catch((error) => {
        notyf.open({
          type: "danger",
          message:
            "Something went wrong. Please try again or contact our support team.",
          duration: 5000,
          ripple: true,
          dismissible: false,
          position: {
            x: "center",
            y: "top",
          },
        });
      })
      .finally(() => {
        actions.setSubmitting(false);
        onHide(); // Close modal
      });

    // TODO: send data to server or API
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Sign Up for Updates</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          isSubmitting,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          values,
        }) => (
          <FormikForm onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Field name="fullName" as={Form.Control} />
                <div className="text-danger mt-1">
                  <ErrorMessage name="fullName" />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Telephone Number</Form.Label>
                <Field name="telephoneNumber" as={Form.Control} />
                <div className="text-danger mt-1">
                  <ErrorMessage name="telephoneNumber" />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Field name="email" as={Form.Control} />
                <div className="text-danger mt-1">
                  <ErrorMessage name="email" />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Country</Form.Label>
                <Field name="country">
                  {({ field }) => (
                    <Form.Select
                      {...field}
                      isInvalid={Boolean(touched.country && errors.country)}
                    >
                      {countryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Field>
                <div className="text-danger mt-1">
                  <ErrorMessage name="country" />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Field name="username" as={Form.Control} />
                <div className="text-danger mt-1">
                  <ErrorMessage name="username" />
                </div>
              </Form.Group>

              <Form.Group controlId="formConsentCheckbox" className="mb-2">
                <Field name="consentMarketing">
                  {({ field }) => (
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="checkbox"
                        {...field}
                        id="consentMarketing"
                        className="me-2"
                        style={{
                          "--dynamic-bg-color": "#0E1A2B",
                          "--dynamic-font-color": "#ffffff",
                        }}
                      />
                      <Form.Label htmlFor="consentMarketing" className="mb-0">
                        I agree to receive marketing communications.
                      </Form.Label>
                    </div>
                  )}
                </Field>
                <div className="text-danger mt-1">
                  <ErrorMessage name="consentMarketing" />
                </div>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                style={{
                  "--dynamic-bg-color": "#0E1A2B",
                  "--dynamic-font-color": "#fff",
                  border: "none",
                }}
                onClick={onHide}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                style={{
                  "--dynamic-bg-color": "#0E1A2B",
                  "--dynamic-font-color": "#fff",
                  border: "none",
                }}
                disabled={isSubmitting}
              >
                Submit
              </Button>
            </Modal.Footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export default LeadSignUp;
